import { inject, Injectable } from '@angular/core'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { parseConfigureJson } from '@app/feature/add-field/add-field-content/add-field-content-common'
import { MultilineCellSettings } from '@app/feature/field-settings/multiline-settings/multiline-settings.component'
import { reduce } from 'lodash-es'
import {
    BaseMainBusinessRecord,
    CellEntities,
    FieldEntities,
    MainBusinessRecord,
    ViewConfiguration,
    ViewConfigurationSettings,
} from '@core/models'
import { ViewDataService } from '@app/views/services/view-data.service'

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class UiRecordsSizingService {
    // Card related sizes
    private readonly CARD_NAME_HEIGHT = 36
    private readonly CARD_BOTTOM_MARGIN = 8
    private readonly FIELD_LABEL_HEIGHT = 20

    // Table related sizes
    private readonly TABLE_HEADER_HEIGHT = 40
    private readonly TABLE_HEADER_BOTTOM_MARGIN = 8
    private readonly TABLE_MARGIN_TOP = 8
    private readonly TABLE_MARGIN_BOTTOM = 16
    private readonly TABLE_ROW_FOOTER_HEIGHT = 56

    // Pixel heights keyed by the multiline "line" setting value.
    // Key 0 = default, while keys 1-3 match the selectable multiline line count.
    private readonly FIELD_HEIGHT_BY_LINES: Record<number, number> = {
        0: 40,
        1: 42,
        2: 64,
        3: 86,
    }

    private readonly CELL_PADDING = 4

    private readonly viewDataService = inject(ViewDataService)

    private viewConfiguration!: ViewConfiguration
    private cells!: { [recordGuid: string]: CellEntities }

    constructor() {
        this.viewDataService.viewDataChanges$.pipe(untilDestroyed(this)).subscribe(() => {
            this.viewConfiguration = this.viewDataService.getViewData().view_configuration
            this.cells = this.viewDataService.getCells()
        })
    }

    getTablesSizes(
        groups: MainBusinessRecord[][],
        fields: FieldEntities,
        visibleFields: string[],
    ): number[] {
        return groups.map((records) => this.getTableSizeByGroup(records, fields, visibleFields))
    }

    getBoardCardsSizes(
        records: BaseMainBusinessRecord[],
        fields: FieldEntities,
        visibleFields: string[],
    ): number[] {
        return records.map((record) => {
            const fieldLabelSpace = this.viewConfiguration.field_name ? this.FIELD_LABEL_HEIGHT : 0

            return (
                this.CARD_NAME_HEIGHT +
                this.getFieldsLength(record, fields, visibleFields, fieldLabelSpace) +
                this.CARD_BOTTOM_MARGIN
            )
        })
    }

    getTableSizeByGroup(
        pagedRecords: MainBusinessRecord[],
        fields: FieldEntities,
        visibleFields: string[],
    ): number {
        const rowsHeight =
            pagedRecords.length === 0 ? 0 : this.getRowHeight(pagedRecords, fields, visibleFields)
        const footer = pagedRecords.length === 0 ? 0 : this.TABLE_ROW_FOOTER_HEIGHT
        const headerMarginBottom = pagedRecords.length === 0 ? 0 : this.TABLE_HEADER_BOTTOM_MARGIN

        return (
            this.TABLE_MARGIN_TOP +
            this.TABLE_HEADER_HEIGHT +
            headerMarginBottom +
            rowsHeight +
            footer +
            this.TABLE_MARGIN_BOTTOM
        )
    }

    /** Returns the guids of fields whose configuration_settings JSON string contains a "line" key,
     *  meaning the field has an explicit multiline height setting applied. */
    private getFieldsWithLineConfiguration(): string[] {
        const configurationSettings = this.viewConfiguration.configuration_settings
        if (!configurationSettings) return []

        return reduce(
            configurationSettings,
            (fieldGuids, setting, fieldGuid) => {
                if (setting.includes('line')) fieldGuids.push(fieldGuid)
                return fieldGuids
            },
            [] as string[],
        )
    }

    /** Returns the total pixel height (px) of all non-empty visible fields on a board card.
     *  When no field has a multiline setting, every field uses the default height.
     *  Otherwise each field uses its own configured height (or the default if unconfigured). */
    private getFieldsLength(
        record: BaseMainBusinessRecord,
        fields: FieldEntities,
        visibleFields: string[],
        fieldLabelSpace: number,
    ): number {
        // Only count fields that have a non-empty cell value and a known field definition
        const fieldsArray = visibleFields.filter(
            (field) => this.cells[record.guid]?.[field]?.value && fields[field],
        )

        const fieldsWithLineConfiguration = this.getFieldsWithLineConfiguration()

        // Fast path: no multiline settings exist, every field gets the default height
        if (fieldsWithLineConfiguration.length === 0) {
            return fieldsArray.reduce((acc) => {
                return acc + fieldLabelSpace + this.FIELD_HEIGHT_BY_LINES[0]
            }, 0)
        }

        return fieldsArray.reduce((acc, fieldGuid) => {
            let fieldHeight = this.FIELD_HEIGHT_BY_LINES[0]
            if (fieldsWithLineConfiguration.includes(fieldGuid)) {
                fieldHeight = this.getLine(fieldGuid, this.viewConfiguration.configuration_settings)
            }

            return acc + fieldHeight + fieldLabelSpace
        }, 0)
    }

    /** Sums the row heights for all records in a table group.
     *  Each row's height equals the tallest field cell in that row (plus cell padding).
     *  When no multiline settings exist every row uses the default field height. */
    private getRowHeight(
        pagedRecords: MainBusinessRecord[],
        fields: FieldEntities,
        visibleFields: string[],
    ): number {
        const fieldsWithLineConfiguration = this.getFieldsWithLineConfiguration()

        return pagedRecords.reduce((groupMaxHeight, record) => {
            // Fast path: no multiline settings, all rows are uniform height
            if (fieldsWithLineConfiguration.length === 0) {
                return groupMaxHeight + this.FIELD_HEIGHT_BY_LINES[0] + this.CELL_PADDING
            }

            const maxFieldHeight = this.getMaxFieldHeightForRecord(
                record,
                fieldsWithLineConfiguration,
                fields,
                visibleFields,
            )

            return groupMaxHeight + maxFieldHeight + this.CELL_PADDING
        }, 0)
    }

    /** Returns the height of the tallest multiline-configured field cell for a single record row.
     *  Only non-empty fields that have an explicit line setting are considered;
     *  the default height is used as the minimum (floor) value. */
    private getMaxFieldHeightForRecord(
        record: MainBusinessRecord,
        fieldsWithLineConfig: string[],
        fields: FieldEntities,
        visibleFields: string[],
    ): number {
        // Non-empty visible fields that also have a multiline line-count setting
        const fieldsArray = visibleFields.filter(
            (field) =>
                this.cells[record.guid]?.[field]?.value &&
                fields[field] &&
                fieldsWithLineConfig.includes(field),
        )

        return fieldsArray.reduce((recordHeight, fieldGuid) => {
            const fieldHeight = this.getLine(
                fieldGuid,
                this.viewConfiguration.configuration_settings,
            )

            return Math.max(recordHeight, fieldHeight)
        }, this.FIELD_HEIGHT_BY_LINES[0])
    }

    /** Resolves the pixel height for a field by reading its "line" value from the stored
     *  JSON configuration string. Falls back to the default height when no setting exists
     *  or the parsed line value is falsy (0 / undefined). */
    private getLine(fieldGuid: string, configurationSettings?: ViewConfigurationSettings): number {
        const setting = configurationSettings?.[fieldGuid]
        if (!setting) return this.FIELD_HEIGHT_BY_LINES[0]

        const line = parseConfigureJson<MultilineCellSettings>(setting)?.line || 0
        return this.FIELD_HEIGHT_BY_LINES[line] ?? this.FIELD_HEIGHT_BY_LINES[0]
    }
}
